/* ==========================================================================
   EZION — CORE
   Behaviour shared by every page: the countdown, the drag rows, quantity
   steppers, AJAX add-to-cart, the toast and the mobile drawer.
   Everything is a custom element or a delegated listener, so sections that
   the theme editor drops in after load pick the behaviour up for free.
   ========================================================================== */

(function () {
  'use strict';

  var EZ = window.EZION || (window.EZION = {});
  EZ.routes = EZ.routes || {};
  EZ.strings = EZ.strings || {};

  /* ----------------------------------------------------------------------
     Money
     ---------------------------------------------------------------------- */

  /**
   * Format cents using the shop's money_format so JS-rendered prices match
   * the Liquid-rendered ones. Falls back to a plain amount if the format
   * string uses a token we don't handle.
   */
  EZ.formatMoney = function (cents) {
    var format = EZ.moneyFormat || '${{amount}}';
    var value = (cents / 100);

    function withDelimiters(number, precision, thousands, decimal) {
      number = (number || 0).toFixed(precision);
      var parts = number.split('.');
      var head = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return parts[1] ? head + decimal + parts[1] : head;
    }

    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_, token) {
      switch (token) {
        case 'amount':                 return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals':     return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator':     return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_space_separator':     return withDelimiters(value, 2, ' ', ',');
        case 'amount_no_decimals_with_space_separator': return withDelimiters(value, 0, ' ', ',');
        default:                       return withDelimiters(value, 2, ',', '.');
      }
    });
  };

  /* ----------------------------------------------------------------------
     Toast
     ---------------------------------------------------------------------- */

  var toastTimer = null;

  EZ.toast = function (message, linkLabel) {
    var el = document.getElementById('EzToast');
    if (!el) return;

    var body = el.querySelector('[data-toast-message]');
    var link = el.querySelector('[data-toast-link]');

    if (body) body.textContent = message;
    if (link) link.hidden = !linkLabel ? true : false;
    if (link && linkLabel) link.textContent = linkLabel;

    el.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.hidden = true; }, 3200);
  };

  /* ----------------------------------------------------------------------
     Cart
     ---------------------------------------------------------------------- */

  /** Push the live item count into every bag badge on the page. */
  EZ.setCartCount = function (count) {
    document.querySelectorAll('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
    });
    document.dispatchEvent(new CustomEvent('ezion:cart:updated', { detail: { count: count } }));
  };

  EZ.refreshCart = function () {
    return fetch(EZ.routes.cart + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        EZ.setCartCount(cart.item_count);
        return cart;
      });
  };

  /**
   * Add a line to the cart. Resolves with Shopify's response, rejects with a
   * message safe to show the shopper (Shopify sends one for sold-out lines).
   *
   * If something has registered sections in EZ.cartSections — the side cart
   * does — they are rendered by this same request and come back on
   * `response.sections`. Asking here rather than in a follow-up fetch is what
   * guarantees the markup reflects the cart *after* the add: a second request
   * is a second chance to race, to be cached, or to fail on its own.
   */
  EZ.addToCart = function (payload) {
    if (EZ.cartSections) {
      payload = Object.assign({}, payload, {
        sections: EZ.cartSections,
        sections_url: window.location.pathname
      });
    }

    return fetch(EZ.routes.cart_add + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || EZ.strings.error);
          return data;
        });
      })
      .then(function (item) {
        return EZ.refreshCart().then(function () { return item; });
      });
  };

  /**
   * What happens after a line lands in the cart. The side cart replaces this
   * with "repaint the panel and slide it open"; on its own the theme falls
   * back to the toast, so a page without the drawer still says something.
   */
  EZ.afterAdd = function (response) {
    EZ.toast(EZ.strings.added || 'Added to bag', EZ.strings.viewBag);
  };

  /* ----------------------------------------------------------------------
     <ez-countdown> — the flickering deal clock
     ---------------------------------------------------------------------- */

  /**
   * Two modes:
   *   data-deadline="2026-09-01T00:00:00Z"  counts to a fixed moment
   *   data-rolls-every="24"                 counts down a repeating window,
   *                                         so the urgency never reads stale
   */
  class EzCountdown extends HTMLElement {
    connectedCallback() {
      this.output = this.querySelector('[data-clock]') || this;
      this.deadline = this.resolveDeadline();
      this.tick();
      this.timer = setInterval(this.tick.bind(this), 1000);
    }

    disconnectedCallback() { clearInterval(this.timer); }

    resolveDeadline() {
      var fixed = this.dataset.deadline;
      if (fixed) {
        var parsed = Date.parse(fixed);
        if (!isNaN(parsed)) return parsed;
      }

      // Rolling window: anchor to the epoch so every visitor in a timezone
      // sees the same clock, and it restarts cleanly when it runs out.
      var hours = parseFloat(this.dataset.rollsEvery || '24');
      var span = Math.max(hours, 1) * 3600 * 1000;
      var now = Date.now();
      return now + (span - (now % span));
    }

    tick() {
      var remaining = this.deadline - Date.now();

      if (remaining <= 0) {
        if (this.dataset.rollsEvery) {
          this.deadline = this.resolveDeadline();
          remaining = this.deadline - Date.now();
        } else {
          this.output.textContent = '00:00:00';
          clearInterval(this.timer);
          this.closest('[data-countdown-host]')?.setAttribute('data-expired', 'true');
          return;
        }
      }

      var total = Math.floor(remaining / 1000);
      var days = Math.floor(total / 86400);
      var hours = Math.floor((total % 86400) / 3600);
      var mins = Math.floor((total % 3600) / 60);
      var secs = total % 60;

      // Past 24 hours the clock rolls the days into the hour slot rather
      // than adding a fourth pair — the design only has room for three.
      var pad = function (n) { return String(n).padStart(2, '0'); };

      // Opt-in via data-days: a launch weeks out reads as "516:23:11" once
      // rolled into hours, so the coming-soon clock breaks the days out.
      if (this.dataset.days !== undefined) {
        this.output.textContent =
          (days > 0 ? days + 'd ' : '') + pad(hours) + ':' + pad(mins) + ':' + pad(secs);
        return;
      }

      this.output.textContent = pad(hours + days * 24) + ':' + pad(mins) + ':' + pad(secs);
    }
  }

  /* ----------------------------------------------------------------------
     <ez-qty> — quantity stepper
     ---------------------------------------------------------------------- */

  class EzQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;

      this.addEventListener('click', function (event) {
        var button = event.target.closest('[data-qty-step]');
        if (!button) return;

        event.preventDefault();
        this.step(parseInt(button.dataset.qtyStep, 10));
      }.bind(this));

      this.input.addEventListener('change', this.clamp.bind(this));
    }

    step(delta) {
      this.input.value = this.bounded(this.value() + delta);
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    clamp() { this.input.value = this.bounded(this.value()); }

    value() { return parseInt(this.input.value, 10) || 1; }

    bounded(n) {
      var min = parseInt(this.input.min, 10) || 1;
      var max = parseInt(this.input.max, 10) || 99;
      return Math.min(Math.max(n, min), max);
    }
  }

  /* ----------------------------------------------------------------------
     <ez-row> — the sideways-dragging product rail
     ---------------------------------------------------------------------- */

  class EzRow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-row-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-row-prev]');
      this.next = this.querySelector('[data-row-next]');

      if (this.prev) this.prev.addEventListener('click', this.scrollBy.bind(this, -1));
      if (this.next) this.next.addEventListener('click', this.scrollBy.bind(this, 1));

      this.track.addEventListener('scroll', this.syncEnds.bind(this), { passive: true });
      window.addEventListener('resize', this.syncEnds.bind(this));
      this.syncEnds();
    }

    /** One nudge moves by a card plus its gap, so cards land on the edge. */
    stride() {
      var card = this.track.firstElementChild;
      if (!card) return 272;
      var gap = parseFloat(getComputedStyle(this.track).columnGap || '20') || 20;
      return card.getBoundingClientRect().width + gap;
    }

    scrollBy(direction, event) {
      if (event) event.preventDefault();
      this.track.scrollBy({ left: direction * this.stride(), behavior: 'smooth' });
    }

    /** Dim an arrow once the rail can't travel any further that way. */
    syncEnds() {
      var max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.toggleAttribute('data-end', this.track.scrollLeft <= 2);
      if (this.next) this.next.toggleAttribute('data-end', this.track.scrollLeft >= max);
    }
  }

  /* ----------------------------------------------------------------------
     Delegated behaviour
     ---------------------------------------------------------------------- */

  /* Coupon codes — "tear off a code" copies to the clipboard. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-copy]');
    if (!trigger) return;

    event.preventDefault();
    var code = trigger.dataset.copy;
    var label = trigger.querySelector('[data-copy-label]') || trigger;
    var original = label.dataset.idleLabel || label.textContent;
    label.dataset.idleLabel = original;

    var done = function () {
      label.textContent = trigger.dataset.copiedLabel || 'copied ✓';
      clearTimeout(trigger._copyTimer);
      trigger._copyTimer = setTimeout(function () { label.textContent = original; }, 1800);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(done);
    } else {
      // Older Safari on iOS — fall back to a throwaway textarea.
      var scratch = document.createElement('textarea');
      scratch.value = code;
      scratch.setAttribute('readonly', '');
      scratch.style.position = 'absolute';
      scratch.style.left = '-9999px';
      document.body.appendChild(scratch);
      scratch.select();
      try { document.execCommand('copy'); } catch (e) { /* nothing else to try */ }
      document.body.removeChild(scratch);
      done();
    }
  });

  /* Quick add straight off a product card. */
  document.addEventListener('submit', function (event) {
    var form = event.target.closest('[data-quick-add]');
    if (!form) return;

    event.preventDefault();

    var button = form.querySelector('[type="submit"]');
    var idle = button ? button.textContent : '';
    var id = form.querySelector('[name="id"]');
    if (!id) return;

    if (button) { button.disabled = true; button.textContent = '…'; }

    EZ.addToCart({ items: [{ id: parseInt(id.value, 10), quantity: 1 }] })
      .then(function (added) {
        // No lasting "in bag" state on the button: it was written into the
        // DOM and never cleared, so removing the item and navigating back
        // (the browser restores the page from cache) still claimed it was in
        // the bag. The drawer and toast confirm the add; the button just
        // returns to its normal label.
        if (button) button.textContent = idle;
        EZ.afterAdd(added);
      })
      .catch(function (error) {
        if (button) button.textContent = idle;
        EZ.toast(error.message || EZ.strings.error, null);
      })
      .then(function () {
        if (button) button.disabled = false;
      });
  });

  /* Mobile drawer. */
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-drawer-toggle]');
    if (toggle) {
      event.preventDefault();
      var drawer = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!drawer) return;
      var open = drawer.hasAttribute('data-open');
      drawer.toggleAttribute('data-open', !open);
      toggle.setAttribute('aria-expanded', String(!open));
      document.body.style.overflow = open ? '' : 'hidden';
      return;
    }

    var close = event.target.closest('[data-drawer-close]');
    if (close) {
      event.preventDefault();
      var host = close.closest('[data-drawer]');
      if (!host) return;
      host.removeAttribute('data-open');
      document.body.style.overflow = '';
      var owner = document.querySelector('[aria-controls="' + host.id + '"]');
      if (owner) owner.setAttribute('aria-expanded', 'false');
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-drawer][data-open]').forEach(function (drawer) {
      drawer.removeAttribute('data-open');
      document.body.style.overflow = '';
      var owner = document.querySelector('[aria-controls="' + drawer.id + '"]');
      if (owner) owner.setAttribute('aria-expanded', 'false');
    });
  });

  /* ----------------------------------------------------------------------
     Register
     ---------------------------------------------------------------------- */

  if (!customElements.get('ez-countdown')) customElements.define('ez-countdown', EzCountdown);
  if (!customElements.get('ez-qty'))       customElements.define('ez-qty', EzQty);
  if (!customElements.get('ez-row'))       customElements.define('ez-row', EzRow);
})();
